import type { ElementClarifyingQuestion, MeasureDateDefaultAnswer, MeasureDateDefaultItem } from 'api/element-issues';

export const MEASURE_DATE_DEFAULTS_TYPE = 'measure_date_defaults';
export const MEASURE_DATE_DEFAULTS_PAGE_SIZE = 5;

/** itemKey → selected date_object_uid. */
export type MeasureDateSelections = Record<string, string>;

/**
 * True when the question renders as the structured per-measure control.
 * Gated on type alone: even with no items, the legacy free-text UI must never
 * post plain text for this type (the backend json-decodes the answer with
 * JSON_THROW_ON_ERROR); the structured control renders an empty state instead.
 */
export const isMeasureDateDefaultsQuestion = (q: ElementClarifyingQuestion): boolean =>
    q.type === MEASURE_DATE_DEFAULTS_TYPE;

/** A live selection only counts while it is still one of the item's options (drift reverts the row). */
export const validSelection = (item: MeasureDateDefaultItem, selections: MeasureDateSelections): string | undefined => {
    const value = selections[item.itemKey];
    return value && item.options.some((o) => o.value === value) ? value : undefined;
};

/**
 * Per-item answers already persisted server-side: partialAnswers of an
 * in-flight question plus the decoded structured `answer` of a completed one.
 * These rows render locked; re-posting a changed value for an answered item
 * is rejected by the backend as a stale conflict.
 */
export const answeredSelections = (q: ElementClarifyingQuestion): MeasureDateSelections => {
    const out: MeasureDateSelections = {};
    const collect = (answers: unknown) => {
        if (!Array.isArray(answers)) return;
        for (const a of answers as Partial<MeasureDateDefaultAnswer>[]) {
            if (
                a &&
                typeof a.itemKey === 'string' &&
                a.itemKey &&
                typeof a.dateObjectUid === 'string' &&
                a.dateObjectUid
            ) {
                out[a.itemKey] = a.dateObjectUid;
            }
        }
    };
    collect(q.partialAnswers);
    if (q.answer) {
        try {
            collect(JSON.parse(q.answer)?.answers);
        } catch {
            // Not a structured answer payload; nothing to restore.
        }
    }
    return out;
};

/** Editable rows start from any backend-marked preselection that is still a valid option. */
export const initialSelections = (
    items: MeasureDateDefaultItem[],
    answered: MeasureDateSelections,
): MeasureDateSelections => {
    const out: MeasureDateSelections = {};
    for (const item of items) {
        if (answered[item.itemKey]) continue;
        const pre = item.selectedValue;
        if (pre && item.options.some((o) => o.value === pre)) {
            out[item.itemKey] = pre;
        }
    }
    return out;
};

/** Submit unlocks only when every row is either already answered or holds a still-valid selection. */
export const isComplete = (
    items: MeasureDateDefaultItem[],
    answered: MeasureDateSelections,
    selections: MeasureDateSelections,
): boolean => items.every((item) => !!answered[item.itemKey] || !!validSelection(item, selections));

export const selectedCount = (
    items: MeasureDateDefaultItem[],
    answered: MeasureDateSelections,
    selections: MeasureDateSelections,
): number => items.filter((item) => !!answered[item.itemKey] || !!validSelection(item, selections)).length;

export const pageCount = (total: number, pageSize: number = MEASURE_DATE_DEFAULTS_PAGE_SIZE): number =>
    Math.max(1, Math.ceil(total / pageSize));

export const pageSlice = <T>(items: T[], page: number, pageSize: number = MEASURE_DATE_DEFAULTS_PAGE_SIZE): T[] =>
    items.slice(page * pageSize, (page + 1) * pageSize);

/**
 * The `answer` string posted through the ordinary answer endpoint. The backend
 * json-decodes it and forwards `answers` to MeasureDateClarificationService::answer;
 * already-answered items are excluded (changing them is a stale conflict server-side).
 */
export const buildSubmitAnswer = (
    items: MeasureDateDefaultItem[],
    answered: MeasureDateSelections,
    selections: MeasureDateSelections,
): string => {
    const answers: MeasureDateDefaultAnswer[] = [];
    for (const item of items) {
        if (answered[item.itemKey]) continue;
        const dateObjectUid = validSelection(item, selections);
        if (dateObjectUid) answers.push({ itemKey: item.itemKey, dateObjectUid });
    }
    return JSON.stringify({ answers });
};
